import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Subscription } from 'rxjs';
import log from 'loglevel';

import {
  GridFilterModel,
  GridLogicOperator,
  GridPaginationMeta,
  GridPaginationModel,
  GridRowsProp,
  GridSortModel,
  GridValidRowModel,
} from '@mui/x-data-grid';

import {
  GraphQLQuery,
  SearchableFilterInput,
  SearchableSortDirection,
  SearchableSortInput,
} from '@lambda-layer/types/filter';
import { GridFilterModelGraphQLQueryMapper } from '@lambda-layer/mappers';

import { buildQuickSearchGraphQLQuery } from '@/components/useServiceDataGrid/quickSearchUtils';
import { UseServiceDataGridParameters } from '@/components/useServiceDataGrid/useServiceDataGrid.types.ts';

/**
 * Custom hook for managing a Data Grid with server-side pagination, sorting, and filtering.
 * It also listens for create, update, and delete events to keep rows fresh in real time.
 */
export function useServiceDataGrid<T extends GridValidRowModel>(
  params: UseServiceDataGridParameters<T> & { quickSearchValue?: string }
) {
  const {
    apiRef,
    service,
    pageSize = 10,
    onQueryUpdated,
    initialSortModel,
    initialFilterModel,
    rowFilter,
    quickSearchValue = '',
    quickSearchFields,
  } = params;

  // ---------------------------------------------------------------------------
  // States: Pagination, Filter, Sort, Data, Loading
  // ---------------------------------------------------------------------------

  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    initialFilterModel ?? {
      items: [],
      logicOperator: GridLogicOperator.And,
    }
  );

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>();
  const [sortField, setSortField] = useState<string>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>();
  const [isReadyToFetch, setIsReadyToFetch] = useState(false);

  const [rows, setRows] = useState<GridRowsProp>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // pageTokenMap maps page -> nextToken for server-side pagination.
  const pageTokenMap = useRef<Record<number, string | undefined>>({});
  const [nextToken, setNextToken] = useState<string>();
  const paginationMetaRef = useRef<GridPaginationMeta>();

  // ---------------------------------------------------------------------------
  // Quick Search Fields (Stable)
  // ---------------------------------------------------------------------------

  const quickSearchFieldsAsStrings = useMemo(
    () => quickSearchFields.map(String),
    [quickSearchFields]
  );

  /**
   * Maintain a stable reference to the quick search fields, so
   * we only see them as changed if the contents really differ.
   */
  const [stableQuickSearch, setStableQuickSearch] = useState(
    quickSearchFieldsAsStrings
  );

  const quickSearchFieldsRef = useRef(
    JSON.stringify(quickSearchFieldsAsStrings)
  );

  useEffect(() => {
    // Update stable fields if they've truly changed
    const newString = JSON.stringify(quickSearchFieldsAsStrings);
    if (newString !== quickSearchFieldsRef.current) {
      quickSearchFieldsRef.current = newString;
      setStableQuickSearch(quickSearchFieldsAsStrings);
    }
  }, [quickSearchFieldsAsStrings]);

  // ---------------------------------------------------------------------------
  // Data Grid Event Subscriptions (Sort & Filter changes)
  // ---------------------------------------------------------------------------

  const handleFilterModelChange = useCallback((model: GridFilterModel) => {
    setFilterModel(model);
  }, []);

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);
    },
    []
  );

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortField(model[0]?.field);
    setSortDirection(model[0]?.sort || undefined);
  }, []);

  useEffect(() => {
    if (!apiRef?.current) return;

    apiRef.current.setPage(0);
    apiRef.current.setPageSize(pageSize);

    if (initialSortModel) {
      apiRef.current.setSortModel(initialSortModel);
    }

    apiRef.current.subscribeEvent(
      'paginationModelChange',
      handlePaginationModelChange
    );
    handlePaginationModelChange({ page: 0, pageSize });

    apiRef.current.subscribeEvent('filterModelChange', handleFilterModelChange);
    handleFilterModelChange(initialFilterModel ?? filterModel);

    apiRef.current.subscribeEvent('sortModelChange', handleSortModelChange);
    handleSortModelChange(initialSortModel ?? []);
  }, [
    apiRef,
    initialSortModel,
    handleFilterModelChange,
    handleSortModelChange,
    pageSize,
    handlePaginationModelChange,
    initialFilterModel,
    filterModel,
  ]);

  // ---------------------------------------------------------------------------
  // Check if ready to fetch data
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (isReadyToFetch || !apiRef?.current) return;

    // Destructure the first element of the sort model (or use an empty object if it's undefined)
    const { field: currentSortField, sort: currentSortDirection } =
      apiRef.current.getSortModel()[0] || {};

    // If there's no sort field/direction, or if they match the current sort model, we're ready to fetch.
    if (
      (!sortField && !sortDirection) ||
      (sortField === currentSortField && sortDirection === currentSortDirection)
    ) {
      setIsReadyToFetch(true);
    }
  }, [apiRef, isReadyToFetch, sortDirection, sortField]);

  // ---------------------------------------------------------------------------
  // Real-Time Subscriptions (Create / Update / Delete)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const subscriptions: Subscription[] = [];

    (async () => {
      // On Update
      const updateSubscription = await service.onUpdateEvent<T>(row => {
        if (rowFilter && !rowFilter(row)) return;
        setRows(prev => {
          if (!prev) return [row];
          const exists = prev.some(r => r.id === row.id);
          return exists
            ? prev.map(r => (r.id === row.id ? row : r))
            : [row, ...prev];
        });
      });
      if (updateSubscription) subscriptions.push(updateSubscription);

      // On Create
      const createSubscription = await service.onCreateEvent<T>(row => {
        if (rowFilter && !rowFilter(row)) return;
        setRows(prev => {
          if (!prev) return [row];
          const exists = prev.some(r => r.id === row.id);
          return exists ? prev : [row, ...prev];
        });
      });
      if (createSubscription) subscriptions.push(createSubscription);

      // On Delete
      const deleteSubscription = await service.onDeleteEvent<T>(row => {
        setRows(prev => prev?.filter(r => r.id !== row.id) ?? []);
      });
      if (deleteSubscription) subscriptions.push(deleteSubscription);
    })();

    return () => {
      subscriptions.forEach(sub => sub.unsubscribe());
    };
  }, [service, rowFilter]);

  // ---------------------------------------------------------------------------
  // Fetch Rows from Server
  // ---------------------------------------------------------------------------

  const fetchRows = useCallback(
    async (
      page: number,
      gqlQuery: GraphQLQuery<T>,
      sort?: SearchableSortInput[]
    ) => {
      setLoading(true);
      const token = pageTokenMap.current[page];

      try {
        const result = await service.search({
          limit: paginationModel?.pageSize ?? pageSize,
          nextToken: token,
          filter: gqlQuery,
          sort,
        });

        setRows(result.items);
        setNextToken(result.nextToken ?? undefined);
        setRowCount(result.total ?? 0);

        // Store nextToken for the next page
        pageTokenMap.current[page + 1] = result.nextToken ?? undefined;
        return result;
      } catch (error) {
        log.error('Error fetching rows:', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [pageSize, paginationModel?.pageSize, service]
  );

  // ---------------------------------------------------------------------------
  // Sort Input Helpers
  // ---------------------------------------------------------------------------

  const getSearchableSortInput = useCallback(
    (sortField: string | undefined, sortDirection: string | undefined) => {
      if (!sortField || !sortDirection) return undefined;
      return [
        {
          field: sortField,
          direction:
            sortDirection === 'asc'
              ? SearchableSortDirection.asc
              : SearchableSortDirection.desc,
        },
      ];
    },
    []
  );

  // ---------------------------------------------------------------------------
  // Main Effect: Whenever filter, sort, or page changes, fetch data
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!isReadyToFetch) return;

    const debounceTimeout = setTimeout(() => {
      const filterMapper = new GridFilterModelGraphQLQueryMapper<T>();

      (async () => {
        let gqlQuery: GraphQLQuery<T> = quickSearchValue
          ? buildQuickSearchGraphQLQuery<T>(quickSearchValue, stableQuickSearch)
          : filterMapper.map(filterModel);

        if (onQueryUpdated) {
          gqlQuery = onQueryUpdated(
            gqlQuery as GraphQLQuery<SearchableFilterInput<T>>
          );
        }

        try {
          await fetchRows(
            paginationModel?.page ?? 0,
            gqlQuery,
            getSearchableSortInput(sortField, sortDirection)
          );
        } catch (err) {
          log.error('Error fetching rows:', err);
        }
      })();
    }, 100);

    return () => clearTimeout(debounceTimeout);
  }, [
    paginationModel?.page,
    filterModel,
    sortField,
    sortDirection,
    quickSearchValue,
    stableQuickSearch,
    fetchRows,
    getSearchableSortInput,
    onQueryUpdated,
    isReadyToFetch,
  ]);

  // ---------------------------------------------------------------------------
  // Track Pagination Metadata
  // ---------------------------------------------------------------------------

  useEffect(() => {
    paginationMetaRef.current = { hasNextPage: !!nextToken };
  }, [nextToken]);

  // ---------------------------------------------------------------------------
  // Return Value
  // ---------------------------------------------------------------------------

  return {
    // Data / loading
    rows,
    loading,
    rowCount,

    // Filter / Sort
    filterModel,
    handleFilterModelChange,

    // Pagination
    paginationMeta: paginationMetaRef.current,
    // Expose pagination to allow controlled usage in DataGrid
    paginationModel,
    handlePaginationModelChange,

    // Additional callback
    onQueryUpdated,
  };
}
